import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Menu, Loader2 } from 'lucide-react';
import AdminSidebar from '../components/admin/AdminSidebar';
import { Button } from '../components/ui/button';
import useAdminInactivityLogout from '../hooks/useAdminInactivityLogout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Auto-logout nach 30 Minuten Inaktivität
  useAdminInactivityLogout();

  // Validate token on mount and periodically
  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        // Validate token by making a simple authenticated request
        await axios.get(`${API}/admin/system/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAuthenticated(true);
      } catch (error) {
        // Token is invalid or expired
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [navigate]);

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
          <p className="text-gray-600">Anmeldung wird überprüft...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, don't render anything (redirect will happen)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <AdminSidebar 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 p-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </Button>
          <img 
            src="https://customer-assets.emergentagent.com/job_musictheory-admin/artifacts/fetmifax_logo_standard.svg" 
            alt="Logo" 
            className="h-8 w-auto"
          />
          <span className="font-bold text-gray-900">MSY Admin</span>
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
